import React, { useEffect, useState, useRef } from "react";
import ProductDetail from "../components/ProductDetail/ProductDetail";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as apis from "../apis";

const categories = {
  ko: [
    { id: "모노머", label: "모노머" },
    { id: "용제", label: "용제" },
    { id: "기타", label: "기타" },
  ],
  en: [
    { id: "Monomers", label: "Monomers" },
    { id: "Solvents", label: "Solvents" },
    { id: "Others", label: "Others" },
  ],
};

export default function ProductDetailContainer() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const koPdfRef = useRef(null);
  const enPdfRef = useRef(null);

  const [data, setData] = useState(null);

  // ✅ 카테고리 선택 상태
  const [selectedKoCategory, setSelectedKoCategory] = useState("");
  const [selectedEnCategory, setSelectedEnCategory] = useState("");

  // pdf 삭제
  const [koRemoveDoc, setKoRemoveDoc] = useState(false);
  const [enRemoveDoc, setEnRemoveDoc] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        const res = await apis.getProductOne(id);
        const item = res?.data?.data;
        setData(item);

        // ✅ 데이터 있을 때 초기값 설정
        if (item?.en?.category && item?.ko?.category) {
          setSelectedKoCategory(item.ko.category);
          setSelectedEnCategory(item.en.category);

          setKoRemoveDoc(false);
          setEnRemoveDoc(false);
        }
      } catch (error) {
        console.error(error);
        alert(
          error.response?.data?.message || "품목을 불러오는데 실패했습니다."
        );
      }
    };
    fetchData();
  }, [id]);

  // ✅ 한국어 카테고리 변경 시 → 영어도 동일 인덱스로 변경 + data 반영
  const handleKoCategoryChange = (e) => {
    const selectedId = e.target.value;
    const index = categories.ko.findIndex((c) => c.id === selectedId);
    const matchedEn = categories.en[index]?.id || categories.en[0].id;

    setSelectedKoCategory(selectedId);
    setSelectedEnCategory(matchedEn);

    // ✅ data 내부 값도 변경
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ko: { ...prev.ko, category: selectedId },
        en: { ...prev.en, category: matchedEn },
      };
    });
  };

  // ✅ 영어 카테고리 변경 시 → 한국어도 동일 인덱스로 변경 + data 반영
  const handleEnCategoryChange = (e) => {
    const selectedId = e.target.value;
    const index = categories.en.findIndex((c) => c.id === selectedId);
    const matchedKo = categories.ko[index]?.id || categories.ko[0].id;

    setSelectedEnCategory(selectedId);
    setSelectedKoCategory(matchedKo);

    // ✅ data 내부 값도 변경
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        en: { ...prev.en, category: selectedId },
        ko: { ...prev.ko, category: matchedKo },
      };
    });
  };

  const handleChange = (locale, e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [locale]: { ...prev?.[locale], [name]: value },
    }));
  };

  const handleKoPdfUpload = () => {
    koPdfRef.current.click();
  };

  const handleEnPdfUpload = () => {
    enPdfRef.current.click();
  };

  /** ✅ PDF 파일 선택 시 */
  const handleKoPdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("PDF 파일만 업로드 가능합니다.");
      e.target.value = "";
      return;
    }

    setKoRemoveDoc(false);
    setData((prev) => ({
      ...prev,
      ko: { ...prev?.ko, doc_path: file },
    }));
  };

  const handleEnPdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("PDF 파일만 업로드 가능합니다.");
      e.target.value = "";
      return;
    }

    setEnRemoveDoc(false);
    setData((prev) => ({
      ...prev,
      en: { ...prev?.en, doc_path: file },
    }));
  };

  const handleDeleteKoPdf = () => {
    setData((prev) => ({
      ...prev,
      ko: { ...prev.ko, doc_path: null },
    }));
    setKoRemoveDoc(true);
    if (koPdfRef.current) koPdfRef.current.value = "";
  };

  // ✅ PDF 삭제 (영문)
  const handleDeleteEnPdf = () => {
    setData((prev) => ({
      ...prev,
      en: { ...prev.en, doc_path: null },
    }));
    setEnRemoveDoc(true);
    if (enPdfRef.current) enPdfRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!data) return alert("데이터가 없습니다.");

    // ✅ 예외 처리 - 필수값 확인
    if (!data.ko?.product_name || !selectedKoCategory) {
      return alert("한국어 제품명과 카테고리는 필수 입력 항목입니다.");
    }

    if (!data.en?.product_name || !selectedEnCategory) {
      return alert("영문 제품명과 카테고리는 필수 입력 항목입니다.");
    }

    const formData = new FormData();

    // ✅ 카테고리
    formData.append("ko_category", selectedKoCategory);
    formData.append("en_category", selectedEnCategory);

    // ✅ JSON 형태로 감싸서 전달
    formData.append(
      "ko",
      JSON.stringify({
        product_name: data.ko?.product_name || "",
        cas_no: data.ko?.cas_no || "",
        pack_unit: data.ko?.pack_unit || "",
        usage_text: data.ko?.usage_text || "",
      })
    );

    formData.append(
      "en",
      JSON.stringify({
        product_name: data.en?.product_name || "",
        cas_no: data.en?.cas_no || "",
        pack_unit: data.en?.pack_unit || "",
        usage_text: data.en?.usage_text || "",
      })
    );

    // ✅ PDF 파일 (File일 때만 전송)
    if (data.ko?.doc_path instanceof File) {
      formData.append("doc_ko", data.ko.doc_path);
    }
    if (data.en?.doc_path instanceof File) {
      formData.append("doc_en", data.en.doc_path);
    }

    if (id) {
      if (koRemoveDoc) {
        formData.append("ko_removeDoc", "true");
      }
      if (enRemoveDoc) {
        formData.append("en_removeDoc", "true");
      }
    }

    try {
      if (id) {
        await apis.updateProduct(id, formData);
        alert("품목이 수정되었습니다.");
      } else {
        await apis.createProduct(formData);
        alert("품목이 생성되었습니다.");
      }
      navigate("/products");
    } catch (error) {
      console.error("품목 저장 실패:", error);
      alert(error.response?.data?.message || "저장 중 오류가 발생했습니다.");
    }
  };

  return (
    <ProductDetail
      id={id}
      data={data}
      categories={categories}
      selectedKoCategory={selectedKoCategory}
      selectedEnCategory={selectedEnCategory}
      handleKoCategoryChange={handleKoCategoryChange}
      handleEnCategoryChange={handleEnCategoryChange}
      handleChange={handleChange}
      koPdfRef={koPdfRef}
      enPdfRef={enPdfRef}
      handleKoPdfUpload={handleKoPdfUpload}
      handleEnPdfUpload={handleEnPdfUpload}
      handleKoPdfChange={handleKoPdfChange}
      handleEnPdfChange={handleEnPdfChange}
      handleDeleteKoPdf={handleDeleteKoPdf}
      handleDeleteEnPdf={handleDeleteEnPdf}
      handleSubmit={handleSubmit}
    />
  );
}
